import styled from '@emotion/styled'
import { Modal, Input, Button, Form } from 'antd'
import type { RuleObject } from 'antd/lib/form'
import type { StoreValue } from 'antd/lib/form/interface'
import BigNumber from 'bignumber.js'
import numeral from 'numeral'
import { useMemo, useState } from 'react'
import { useAccount, useBalance } from 'wagmi'

import { useLocalStorage } from '@/context/localstorage-context'
import type { PosStaker } from '@/types/pos-staker'
import { checkAmountUnit, getRewardTime } from '@/utils'

import DelegateConfirm from './delegate-confirm'

export default function DelegateModal({
  type,
  visible,
  onCancel,
  stakerInfo,
  action,
}: {
  type: string
  visible: boolean
  onCancel: () => void
  stakerInfo: PosStaker
  action: (v: string) => void
}) {
  const [showConfirm, setShowConfirm] = useState(false)
  const { address } = useAccount()
  const { data, status } = useBalance({ address })

  const [form] = Form.useForm()
  const { theme } = useLocalStorage()
  const isdark = useMemo(() => theme === 'dark', [theme])

  const balance = useMemo(() => {
    return data && status === 'success' ? data.formatted : '0'
  }, [data, status])

  const checkAmount = async (rule: RuleObject, value: StoreValue) => {
    try {
      if (!checkAmountUnit(value) || new BigNumber(value).lte(0)) {
        return Promise.reject(new Error('Please enter correct amount!'))
      }
      if (new BigNumber(value).gte(balance)) {
        return Promise.reject(new Error('Please enter correct amount!'))
      }
      const quota = numeral(stakerInfo.quotaLeft || '0').value()
      if (new BigNumber(value || '0').gt(quota || '0')) {
        return Promise.reject(
          new Error('The amount entered exceeds the quota!'),
        )
      }
      if (
        (new BigNumber(value).lt(100) && type === 'Delegate') ||
        new BigNumber(value).lt(0.0001)
      ) {
        return Promise.reject(
          new Error(
            `Minimum amount should be ${
              type === 'Delegate' ? '100' : '0.0001'
            } WAN`,
          ),
        )
      }
      return Promise.resolve()
    } catch {
      return Promise.reject(new Error('Please enter correct amount!'))
    }
  }

  const onOk = () => {
    form
      .validateFields()
      .then(() => {
        setShowConfirm(true)
      })
      .catch((error) => {
        console.log(error, 'onOk')
      })
  }

  return (
    <Container>
      <Modal
        className={isdark ? 'darkModal' : 'lightModal'}
        maskClosable={true}
        closable={false}
        destroyOnClose={true}
        style={{ marginTop: '8%', borderRadius: '16px' }}
        width={700}
        mask={true}
        open={visible}
        title={
          <Title className="text-light-0 dark:text-white">
            {type} WAN with {stakerInfo.name}
          </Title>
        }
        footer={null}
        onCancel={onCancel}
      >
        <Form form={form} name="delegate">
          <Form.Item
            name="amount"
            rules={[{ required: true, validator: checkAmount }]}
          >
            <Input />
          </Form.Item>
        </Form>
        <AddrInfo>
          <span>Balance</span>
          <span>
            <Data>
              {numeral(balance)
                .format('0,0.00000')
                .replace(/\.?0+$/, '')}
            </Data>{' '}
            WAN
          </span>
        </AddrInfo>
        <AddrInfo>
          <span>Delegating from</span>
          <AddrText>{address}</AddrText>
        </AddrInfo>
        <BtnArea isdark={isdark}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button onClick={onOk}>OK</Button>
        </BtnArea>
        <Info isdark={isdark}>
          <div>Notice</div>
          <ul style={{ listStyleType: 'disc' }}>
            <li>
              Delegated WAN will begin to accumulate rewards at{' '}
              {getRewardTime()}.
            </li>
            <li>
              If your delegated node is selected and operating normally, rewards
              will be automatically sent to your original wallet address daily.
            </li>
            <li>
              Upon undelegating your WAN, the funds will be returned to your
              original wallet address within 3 days.
            </li>
          </ul>
        </Info>
      </Modal>
      {showConfirm && (
        <DelegateConfirm
          visible={showConfirm}
          onCancel={() => setShowConfirm(false)}
          stakerInfo={stakerInfo}
          action={action}
          amount={form.getFieldValue('amount')}
        />
      )}
    </Container>
  )
}

const Container = styled.div`
  .ant-input {
    border: 1px solid #2fbdf4;
  }
`

const Title = styled.span`
  font-size: 24px;
  font-family: Inter-Bold, Inter;
  font-weight: 400;
`

const AddrInfo = styled.div`
  display: flex;
  color: #757575;
  font-size: 14px;
  justify-content: space-between;

  span {
    color: #818d96;
  }
`

const Data = styled.span`
  color: #2fbdf4 !important;
`

const BtnArea = styled.div<{
  isdark: boolean
}>`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  gap: 20px;

  .ant-btn:first-of-type {
    background: ${(props) => (props.isdark ? '#1E3546' : '#E5E5E5')};
    border-radius: 16px;
    color: ${(props) => (props.isdark ? '#818D96' : '##999999')};
  }
  .ant-btn:first-of-type:hover {
    background: ${(props) => (props.isdark ? '#1E3546' : '#E5E5E5')};
  }
  .ant-btn {
    width: calc((100% - 20px) / 2);
    border-radius: 16px;
    height: 60px;
    color: white;
    border: none;
    background: #0f68aa;
  }
  .ant-btn:hover {
    background: #0f68aa;
  }
`

const Info = styled.div<{
  isdark: boolean
}>`
  font-size: 14px;
  font-family: Inter-Regular, Inter;
  font-weight: 400;
  color: ${(props) => (props.isdark ? '#818d96' : '#999')};
  margin-top: 20px;
  border-radius: 16px;
  border: ${(props) =>
    props.isdark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #F5F5F5'};
  padding: 15px;
  text-align: left;
  background-color: ${(props) => (props.isdark ? '#051F31' : '#F5F5F5')};

  ul {
    padding-left: 1rem;
  }
`

const AddrText = styled.span`
  @media (max-width: 768px) {
    width: 60%;
    text-align: right;
  }
`
